import { Text, VStack } from "@chakra-ui/layout";
import { Stat, StatGroup, StatLabel, StatNumber } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { CreditFacilityEventLayout } from "features/credit-facility/CreditFacilityEventLayout";
import { CreditFacilitySubpageProps } from "features/credit-facility/CreditFacilitySubpageProps";
import {
  getCreditFacilityMergePrincipalTranchesRetrieveQueryKey,
  getCreditFacilityMergePrincipalTranchesSummaryTableListQueryKey,
  useCreditFacilityMergePrincipalTranchesCreate,
  useCreditFacilityMergePrincipalTranchesRetrieveSuspense,
} from "gen/endpoints/credit-facility/credit-facility";
import { CreditFacilityMergeTranchesUpsertDataRequest } from "gen/model/creditFacilityMergeTranchesUpsertDataRequest";
import { CreditFacilityMergeTrancheUpsertData } from "gen/model/creditFacilityMergeTrancheUpsertData";
import { CreditFacilityTrancheUseMetaData } from "gen/model/creditFacilityTrancheUseMetaData";
import { GenericTableRow } from "gen/model/genericTableRow";
import { MoneyTableCellValue } from "gen/model/moneyTableCellValue";
import { TextTableCellValue } from "gen/model/textTableCellValue";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import Form from "shared/components/form/Form";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxStats } from "shared/components/table/box/TableBoxStats";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { getSelectedChildrenRows } from "shared/components/table/data/DataTableUtils";
import { formatMoneyFromDecimal } from "shared/utils/MoneyUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export const CreditFacilityMergeTranchesSelectPage: React.FC = () => {
  const { t } = useTranslation(["creditFacility", "common"]);
  const { navigateNext } = usePageWizard();
  const queryClient = useQueryClient();

  const { fundFamilyId, creditFacilityId, fundFamilyEvent } =
    useOutletContext<CreditFacilitySubpageProps>();

  const { data: tranchesTable } =
    useCreditFacilityMergePrincipalTranchesRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
      fundFamilyEvent.id!,
    );

  const childRowById: Record<string, GenericTableRow> = tranchesTable.rows
    .flatMap((row) => row.child_rows)
    .reduce<
      Record<string, GenericTableRow>
    >((acc, child_row) => (child_row ? { ...acc, [child_row.id]: child_row } : acc), {});
  // this is all of the entities and their selected state
  const [rowSelectionState] = useState<Record<string, boolean>>(
    getSelectedChildrenRows(tranchesTable.rows),
  );

  // this is only the selected entities
  const [selectedState, setSelectedState] = useState<Record<string, boolean>>(
    Object.keys(rowSelectionState).reduce(
      (selectedState, id: string) => ({
        ...selectedState,
        ...(rowSelectionState[id] ? { [id]: true } : {}),
      }),
      {},
    ),
  );

  const { mutateAsync: createMergeTranches } =
    useCreditFacilityMergePrincipalTranchesCreate({
      mutation: {
        onSuccess: async () => {
          await invalidateQueryKeys(
            queryClient,
            getCreditFacilityMergePrincipalTranchesRetrieveQueryKey(
              fundFamilyId,
              creditFacilityId,
              fundFamilyEvent.id!,
            ),
            getCreditFacilityMergePrincipalTranchesSummaryTableListQueryKey(
              fundFamilyId,
              creditFacilityId,
              fundFamilyEvent.id!,
            ),
          );
        },
      },
    });
  const form = useForm<CreditFacilityMergeTranchesUpsertDataRequest>({});

  const onSubmit = async () => {
    const upsertData: CreditFacilityMergeTrancheUpsertData[] = Object.keys(
      selectedState,
    ).map((id) => {
      const metadata = childRowById[id]
        .metadata as CreditFacilityTrancheUseMetaData;
      return {
        investment_id: metadata.investment_id,
        loan_id: metadata.loan_id,
        principal_tranche_id: metadata.tranche_id,
        transaction_code_id: metadata.transaction_code_id,
      };
    });
    await createMergeTranches({
      fundFamilyId,
      creditFacilityId,
      fundFamilyEventId: fundFamilyEvent.id!,
      data: { data: upsertData },
    });
  };

  return (
    <CreditFacilityEventLayout canEditEvent={true}>
      <Form form={form} onSubmit={onSubmit} onSuccess={navigateNext}>
        <Text mb="5px">
          {t("creditFacility:merge.selectTranches.description")}
        </Text>
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>
              {t("creditFacility:merge.selectTranches.breadcrumb")}
            </TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableTrancheStats
            selectedRows={Object.keys(selectedState).map(
              (id) => childRowById[id],
            )}
          />
          <TableBoxBody>
            <DataTable
              data={tranchesTable}
              rowSelection={rowSelectionState}
              onRowSelectionChange={setSelectedState}
            />
          </TableBoxBody>
        </TableBox>
        <PageWizardFormFooter />
      </Form>
    </CreditFacilityEventLayout>
  );
};

const TableTrancheStats: React.FC<{ selectedRows: GenericTableRow[] }> = ({
  selectedRows,
}) => {
  const { t } = useTranslation(["creditFacility", "common"]);
  const newTrancheData = Object.values(
    selectedRows.reduce<
      Record<
        string,
        { newTrancheAmount: Decimal; newTrancheLoanName: string } | undefined
      >
    >((acc, row) => {
      const loanId =
        (row.metadata as CreditFacilityTrancheUseMetaData).loan_id || "";
      // If the loan_id is not in acc, add it with initial values
      if (!acc[loanId]) {
        acc[loanId] = {
          newTrancheAmount: new Decimal(0),
          newTrancheLoanName:
            (row.cells.loan_type.value as TextTableCellValue).text || "",
        };
      }

      // Add the fund_family_amount.amount to the existing sum
      acc[loanId].newTrancheAmount = acc[loanId].newTrancheAmount.add(
        new Decimal(
          (row.cells.fund_family_total.value as MoneyTableCellValue).amount ||
            "0.00",
        ),
      );
      return acc;
    }, {}),
  ) as { newTrancheAmount: Decimal; newTrancheLoanName: string }[];

  return (
    <TableBoxStats>
      <VStack alignItems="flex-start">
        <Text
          textStyle="small"
          fontWeight={600}
          textColor="gray.400"
          textTransform="uppercase"
        >
          {t("creditFacility:merge.selectTranches.newTranches")}
        </Text>
        <StatGroup>
          {newTrancheData.length ? (
            newTrancheData.map(({ newTrancheAmount, newTrancheLoanName }) => (
              <Stat key={newTrancheLoanName} textStyle="bodyBold" size="xs">
                <StatNumber>
                  {formatMoneyFromDecimal(newTrancheAmount)}
                </StatNumber>
                <StatLabel>{newTrancheLoanName}</StatLabel>
              </Stat>
            ))
          ) : (
            <Stat textStyle="bodyBold" size="xs">
              <StatNumber>
                {formatMoneyFromDecimal(new Decimal("0.00"))}
              </StatNumber>
              <StatLabel>-</StatLabel>
            </Stat>
          )}
        </StatGroup>
      </VStack>
    </TableBoxStats>
  );
};
